import type { DictionaryEntry } from "@/stores/dictionary";

/**
 * Wiktionary-backed dictionary API — https://github.com/suvankar-mitra/free-dictionary-rest-api
 *
 * Replaces api.dictionaryapi.dev, which is unreachable from browsers on some
 * networks (it hangs ~20s and then fails with `TypeError: Failed to fetch`) and
 * is slow to the point of being unusable for many ordinary words.
 *
 * Note the response is NOT the old api.dictionaryapi.dev shape: definitions
 * live at `meanings[].senses[].glosses[]`, not `meanings[].definitions[]`.
 */
const DICTIONARY_API_BASE = "https://api.suvankar.cc/dictionaryapi/v1/definitions/en";

/**
 * Per-attempt cap on the upstream request, so one slow word cannot leave the
 * panel spinning. Responses here are consistently well under this.
 */
export const UPSTREAM_TIMEOUT_MS = 8000;

const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 400;

/** Wiktionary lists a lot of synonyms; the UI shows five. */
const MAX_SYNONYMS = 10;

/** Thrown when the lookup genuinely failed, as opposed to finding no entry. */
export class DictionaryLookupError extends Error {
    constructor(
        message: string,
        readonly code: string,
    ) {
        super(message);
        this.name = "DictionaryLookupError";
    }
}

interface SuvankarSense {
    glosses?: string[];
    synonyms?: string[];
    antonyms?: string[];
    examples?: string[];
}

interface SuvankarMeaning {
    partOfSpeech?: string;
    senses?: SuvankarSense[];
    etymologyText?: string;
}

interface SuvankarEntry {
    word?: string;
    lang?: string;
    ipa?: string;
    audioUrl?: string;
    meanings?: SuvankarMeaning[];
}

/**
 * Translate the upstream payload into the `DictionaryEntry` the UI renders.
 *
 * Each gloss in a sense becomes its own definition; the sense's first example
 * is attached to each, which is what the definition panel renders under the
 * gloss in a left-bordered block.
 *
 * @returns `null` when there is nothing renderable, which the caller reports as
 * a miss rather than showing an empty card.
 */
export function normalizeEntry(raw: unknown, requestedWord: string): DictionaryEntry | null {
    if (!raw || typeof raw !== "object") return null;
    const source = raw as SuvankarEntry;

    const meanings = (Array.isArray(source.meanings) ? source.meanings : [])
        .filter((m) => m && Array.isArray(m.senses))
        .map((m) => ({
            partOfSpeech: m.partOfSpeech ?? "",
            definitions: (m.senses ?? []).flatMap((sense) => {
                const glosses = Array.isArray(sense?.glosses) ? sense.glosses : [];
                const example = Array.isArray(sense?.examples) ? sense.examples[0] : undefined;
                const synonyms = Array.isArray(sense?.synonyms)
                    ? sense.synonyms.slice(0, MAX_SYNONYMS)
                    : undefined;
                const antonyms = Array.isArray(sense?.antonyms)
                    ? sense.antonyms.slice(0, MAX_SYNONYMS)
                    : undefined;

                return glosses
                    .filter((g): g is string => typeof g === "string" && g.length > 0)
                    .map((definition) => ({ definition, example, synonyms, antonyms }));
            }),
        }))
        .filter((m) => m.definitions.length > 0);

    if (meanings.length === 0) return null;

    // The upstream carries IPA and audio at the top level rather than per
    // meaning, so they are lifted into the `phonetics` array the UI reads.
    const phonetics = (source.ipa || source.audioUrl)
        ? [{ text: source.ipa, audio: source.audioUrl }]
        : [];

    return {
        word: source.word || requestedWord,
        phonetic: source.ipa,
        phonetics,
        // Etymology rides along on the first meaning that has one.
        origin: meanings.length ? findEtymology(source.meanings) : undefined,
        meanings,
    };
}

function findEtymology(meanings: SuvankarMeaning[] | undefined): string | undefined {
    for (const m of meanings ?? []) {
        if (m && typeof m.etymologyText === "string" && m.etymologyText.length > 0) {
            return m.etymologyText;
        }
    }
    return undefined;
}

function isAbortError(err: unknown): boolean {
    return err instanceof Error &&
        (err.name === "TimeoutError" || err.name === "AbortError");
}

/**
 * This API answers an unknown word with **500**, not 404 — the lookup throws
 * server-side and Spring returns its default error body. Without this check
 * every nonsense search would retry and then report "could not reach the
 * dictionary" instead of the honest "no definition found".
 */
function isNotFoundBody(status: number, body: unknown): boolean {
    if (status !== 500 || !body || typeof body !== "object") return false;
    const b = body as Record<string, unknown>;
    return b.status === 500 && typeof b.error === "string" && typeof b.path === "string";
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Combine the caller's abort signal with our own timeout, without relying on
 * `AbortSignal.any` (still unevenly supported across mobile Safari).
 */
function withTimeout(signal?: AbortSignal): { signal: AbortSignal; done: () => void } {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UPSTREAM_TIMEOUT_MS);

    const onAbort = () => controller.abort();
    if (signal) {
        if (signal.aborted) controller.abort();
        else signal.addEventListener("abort", onAbort, { once: true });
    }

    return {
        signal: controller.signal,
        done: () => {
            clearTimeout(timer);
            signal?.removeEventListener("abort", onAbort);
        },
    };
}

/**
 * Look up an English definition.
 *
 * @returns the entry, or `null` when the word has no definition.
 * @throws {DictionaryLookupError} when the lookup itself failed. The message is
 * user-facing — the dictionary panel renders it verbatim.
 */
export async function lookupEnglishDefinition(
    word: string,
    signal?: AbortSignal,
): Promise<DictionaryEntry | null> {
    const url = `${DICTIONARY_API_BASE}/${encodeURIComponent(word)}`;
    const { signal: requestSignal, done } = withTimeout(signal);

    try {
        let response: Response;
        try {
            response = await fetch(url, {
                headers: { Accept: "application/json" },
                signal: requestSignal,
            });
        } catch (err) {
            // A caller-driven abort is a cancellation, not a failure: the store
            // drops an in-flight lookup whenever a new keystroke wins.
            if (signal?.aborted) return null;
            if (isAbortError(err)) {
                throw new DictionaryLookupError(
                    "The dictionary took too long to respond.",
                    "timeout",
                );
            }
            throw new DictionaryLookupError(
                "Could not reach the dictionary. Please try again.",
                "network",
            );
        }

        if (signal?.aborted) return null;

        if (response.ok) {
            return normalizeEntry(await response.json(), word);
        }

        // Unknown word — see isNotFoundBody.
        const errorBody = await response.json().catch(() => null);
        if (response.status === 404 || isNotFoundBody(response.status, errorBody)) {
            return null;
        }

        if (response.status === 429) {
            throw new DictionaryLookupError(
                "The dictionary is busy. Please try again in a moment.",
                "rate_limited",
            );
        }

        // Genuine 5xx: worth one more go, since these are usually transient.
        if (response.status >= 500) {
            for (let attempt = 1; attempt < MAX_ATTEMPTS; attempt++) {
                await sleep(RETRY_DELAY_MS);
                if (signal?.aborted) return null;
                try {
                    const retry = await fetch(url, {
                        headers: { Accept: "application/json" },
                        signal: AbortSignal.timeout(UPSTREAM_TIMEOUT_MS),
                    });
                    if (retry.ok) return normalizeEntry(await retry.json(), word);
                    const retryBody = await retry.json().catch(() => null);
                    if (isNotFoundBody(retry.status, retryBody)) return null;
                } catch (err) {
                    if (signal?.aborted) return null;
                    if (isAbortError(err)) break;
                }
            }
        }

        throw new DictionaryLookupError(
            "Could not reach the dictionary. Please try again.",
            "upstream_error",
        );
    } finally {
        done();
    }
}

/**
 * The IPA transcription for a word, or `null` when there is none.
 *
 * Never throws: caption phonetics decorate words across a whole subtitle track,
 * so one unavailable word must not break the rest of the line.
 */
export async function lookupEnglishIpa(
    word: string,
    signal?: AbortSignal,
): Promise<string | null> {
    try {
        const entry = await lookupEnglishDefinition(word, signal);
        return entry?.phonetic ?? null;
    } catch {
        return null;
    }
}
